import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Navigate,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router';

import { RouterErrorFallback } from '@/components/errors/router';
import { PageLoader } from '@/components/ui/pageloader';
import { paths } from '@/config/paths';

import { ProtectedRoute } from './routes/protected';

// Create a route that requires authentication
const protect = (route: RouteObject): RouteObject => ({
  element: <ProtectedRoute />,
  children: [route],
});

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      HydrateFallback: () => <PageLoader show />,
      ErrorBoundary: RouterErrorFallback,
      children: [
        // Landing route
        {
          path: paths.home.path,
          lazy: () => import('./routes/landing').then(convert(queryClient)),
        },

        // Authentication routes
        // NOTE: In bullet-proof react, there is a `ProtectedRoute` for user authentication
        // using the `react-query-auth` package. However, GlyComb does not follow this
        // because it uses the Google authentication mechanism and does not call the APIs
        // from the browser during user registration and login.
        {
          path: paths.auth.login.path,
          loader: async ({ request }) => {
            const url = new URL(request.url);
            const token = url.searchParams.get('token') ?? '';
            const errorCode = url.searchParams.get('errcode') ?? '';
            return {
              isSuccess: token !== '' && errorCode === '',
              errorCode: errorCode !== '' ? errorCode : undefined,
            };
          },
          lazy: () => import('./routes/auth/login').then(convert(queryClient)),
        },
        {
          path: paths.auth.logout.path,
          lazy: () => import('./routes/auth/logout').then(convert(queryClient)),
        },

        // Input and Search routes
        protect({
          path: paths.input.text.path,
          lazy: () => import('./routes/text-input').then(convert(queryClient)),
        }),
        {
          path: paths.search.text.path,
          lazy: () => import('./routes/text-search').then(convert(queryClient)),
        },

        // Glycan routes
        {
          path: paths.glycan.root.path,
          children: [
            {
              path: paths.glycan.list.path,
              lazy: () =>
                import('./routes/glycan/list').then(convert(queryClient)),
            },
            {
              path: paths.glycan.detail.path,
              lazy: () =>
                import('./routes/glycan/detail').then(convert(queryClient)),
            },
          ],
        },

        protect({
          path: paths.user.root.path,
          children: [
            // User profile route
            {
              path: paths.user.profile.path,
              lazy: () =>
                import('./routes/user/user-profile').then(convert(queryClient)),
            },

            // User submission route
            {
              path: paths.user.submission.path,
              lazy: () =>
                import('./routes/user/user-submissions').then(
                  convert(queryClient),
                ),
            },

            // User submission detail route
            {
              path: paths.user.submissionDetail.path,
              lazy: () =>
                import('./routes/user/user-submission-detail').then(
                  convert(queryClient),
                ),
            },
          ],
        }),

        {
          path: paths.feedback.path,
          lazy: () => import('./routes/feedback').then(convert(queryClient)),
        },

        // Legacy routes -- misc
        {
          path: paths.legacy.byonicConverter.path,
          element: (
            <Navigate to={paths.tools.byonicConverter.getHref()} replace />
          ),
        },
        {
          path: paths.legacy.userProfile.path,
          element: <Navigate to={paths.user.profile.getHref()} replace />,
        },

        // Not found
        {
          path: '*',
          lazy: () => import('./routes/not-found').then(convert(queryClient)),
        },
      ],
    },
  ]);

const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};

AppRouter.displayName = 'AppRouter';

export { AppRouter };
